// @flow
import { ApiClient, EvaluationApi, EvaluationBatchRequest } from 'flagr';
import { find, propEq, curry } from 'ramda';
import type { User } from './auth';
import { reduce } from '../helpers/utils';
import config from '../../config/config';
import { notify } from '../helpers/BugsnagClient';

const debug = require('debug')('jp:stores:features');

const LOAD = 'features/LOAD';
const LOAD_SUCCESS = 'features/LOAD_SUCCESS';
const LOAD_FAIL = 'features/LOAD_FAIL';

type Flag = {| key: string, name: string |};
type Flags = Array<Flag>;

export type Feature = {|
  key: string,
  enabled: boolean,
  segmentID?: ?number,
  variantID?: ?number,
  variantKey?: ?string,
  variantAttachment?: ?any
|};

type RawFeature = {|
  flagID: number,
  flagKey: string,
  segmentID: ?number,
  variantID: ?number,
  variantKey: ?string,
  variantAttachment: any,
  evalContext: {
    entityType: string,
    flagID: number,
    entityID: string,
    entityContext: any
  },
  timestamp: string,
  flagSnapshotID: number,
  evalDebugLog: any
|};

export type State = {|
  loaded: boolean,
  loading: boolean,
  features: Array<Feature>
|};

const FLAGS: Flags = [
  { key: 'kshb778sw8c5i16gc', name: 'hotjar_enabled' },
  {
    key: 'jp_web_disable_walking_times_display',
    name: 'jp_web_disable_walking_times_display'
  },
  {
    key: 'home_banner',
    name: 'home_banner'
  },
  {
    key: 'google_recaptcha_use_v2',
    name: 'google_recaptcha_use_v2'
  },
  {
    key: 'zendesk_chat_widget',
    name: 'zendesk_chat_widget'
  },
  {
    key: 'solvemate_widget',
    name: 'solvemate_widget'
  },
  {
    key: 'checkout_banner',
    name: 'checkout_banner'
  },
  {
    key: 'disable_jp_web_google_pay',
    name: 'disable_jp_web_google_pay'
  },
  {
    key: 'disable_jp_web_apple_pay',
    name: 'disable_jp_web_apple_pay'
  },
  {
    key: 'disable_jp_web_apple_sign_in',
    name: 'disable_jp_web_apple_sign_in'
  },
  {
    key: 'fancy_banner',
    name: 'fancy_banner'
  },
  {
    key: 'monthly_bookings_rolling_only',
    name: 'monthly_bookings_rolling_only'
  },
  {
    key: 'jp_web_disable_landing_page_control_trustpilot',
    name: 'jp_web_disable_landing_page_control_trustpilot'
  },
  {
    key: 'disable_jp_web_route_policy_page',
    name: 'disable_jp_web_route_policy_page'
  },
  {
    key: 'disable_jp_web_route_dashboard_tickets_and_permits',
    name: 'disable_jp_web_route_dashboard_tickets_and_permits'
  },
  {
    key: 'preload_assets',
    name: 'preload_assets'
  },
  {
    key: 'disable_jp_web_route_dashboard_booking_received',
    name: 'disable_jp_web_route_dashboard_booking_received'
  }
];

const flagKeys = FLAGS.map((flag: Flag) => flag.key);

const findBy = curry((propName: string, propValue: any) =>
  find(propEq(propName, propValue))
);
const findFlagByKey = findBy('key');
const findFeatureByKey = findBy('key');

const isRawFeatureEnabled = (feature: RawFeature): boolean =>
  feature.segmentID !== undefined &&
  feature.variantID !== undefined &&
  feature.variantKey !== undefined;

export const initialState: State = {
  loaded: false,
  loading: false,
  features: []
};

type GlobalState = { features: State };

type Action =
  | {| type: typeof LOAD |}
  | {| type: typeof LOAD_SUCCESS, payload: any |}
  | {| type: typeof LOAD_FAIL, payload: any |}
  | {|
      types: Array<string>,
      params?: any,
      promise: (?ApiClient) => Promise<{}>
    |};

export default reduce<State, Action>(initialState, {
  [LOAD](state: State): State {
    debug(LOAD);
    return { ...state, loading: true };
  },
  [LOAD_SUCCESS](state: State, action): State {
    debug(LOAD_SUCCESS, action);
    if (!action.payload && !action.payload.evaluationResults) {
      return state;
    }

    const features = action.payload.evaluationResults
      .map(
        (feature: RawFeature): ?Feature => {
          if (!config.isProduction) {
            debug(feature.flagID, feature.evalDebugLog);
          }
          const flag: Flag = findFlagByKey(feature.flagKey)(FLAGS);
          if (!flag) {
            return false;
          }
          return {
            key: flag.name,
            enabled: isRawFeatureEnabled(feature),
            variantKey: feature.variantKey,
            variantID: feature.variantID,
            variantAttachment: feature.variantAttachment,
            segmentID: feature.segmentID
          };
        }
      )
      .filter(elem => typeof elem === 'object');

    return {
      ...state,
      loading: false,
      loaded: true,
      features: [...state.features, ...features]
    };
  },
  [LOAD_FAIL](state: State, action): State {
    debug(LOAD_FAIL, action);
    return { ...state, loading: false, loaded: false };
  }
});

export { default as isFeatureEnabled } from '../helpers/isFeatureEnabled';

export function getFeatureFromState(
  features: State,
  featureName: string
): ?Feature {
  return findFeatureByKey(featureName)(features.features);
}

const buildBatchRequest = (identifier, userLoggedIn, user, fingerprint) => {
  debug(`Using identifier: ${identifier}`);

  const entityContext = {
    userLoggedIn: Boolean(userLoggedIn).toString(),
    identifier,
    useragent: fingerprint.components.useragent,
    geoip: fingerprint.components.geoip,
    userId: user?.id,
    isSuperuser: user?.isSuperuser,
    isAdmin: user?.isAdmin
  };

  debug('Using entity context', entityContext);

  const evalRequest = new EvaluationBatchRequest([
    {
      entityId: identifier,
      entityType: 'user',
      entityContext
    }
  ]);
  evalRequest.flagKeys = flagKeys;
  if (!config.isProduction) {
    evalRequest.enableDebug = true;
  }
  return evalRequest;
};

export function load(user: ?User, fingerprint: any, entityId: string) {
  debug(`Using basePath: ${config.featureApiEndpoint}`);

  const apiClient = new ApiClient();
  apiClient.basePath = config.featureApiEndpoint;

  const apiInstance = new EvaluationApi(apiClient);
  let userLoggedIn = false;
  const identifier = entityId;
  if (user) {
    userLoggedIn = !!user.id;
  }

  return (dispatch: Action => void, getState: () => GlobalState) => {
    if (config.isTesting) {
      return Promise.resolve();
    }

    if (!getState().features.loading && !getState().features.loaded) {
      return dispatch({
        types: [LOAD, LOAD_SUCCESS, LOAD_FAIL],
        promise: () =>
          new Promise((resolve, reject) => {
            apiInstance.postEvaluationBatch(
              buildBatchRequest(identifier, userLoggedIn, user, fingerprint),
              (error, data) => {
                if (error) {
                  notify(error, {});
                  reject(new Error('Error getting features from FEATURE API'));
                } else {
                  resolve({ ...data });
                }
              }
            );
          })
      });
    }
    return Promise.resolve(getState().features.features);
  };
}
